import { formatDistanceToNow } from 'date-fns';
import { listActiveBatches } from '../api';
import { usePolling } from '../hooks/usePolling';
import type { BatchSummary } from '../types';

/**
 * "Active Batch Runs" panel — lightning/interior parity for the video app,
 * restyled in the video palette. Renders nothing when no batch has live
 * jobs, so the Live page is unchanged for anyone not using CSV uploads.
 *
 * The percentages come straight from the server
 * (`video.jobs.JobStore.batch_summary`) so this stays a pure view.
 */

function ProgressBar({ percentage }: { percentage: number }) {
  return (
    <div className="flex items-center gap-2 min-w-[8rem]">
      <div className="flex-1 h-2 bg-primary-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-brand-gradient transition-all duration-300"
          style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
        />
      </div>
      <span className="text-xs font-medium text-primary-700 tabular-nums min-w-[2.5rem] text-right">
        {percentage.toFixed(0)}%
      </span>
    </div>
  );
}

function StatusSummary({ batch }: { batch: BatchSummary }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {batch.completed > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">
          {batch.completed} done
        </span>
      )}
      {batch.failed > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium">
          {batch.failed} failed
        </span>
      )}
      {batch.in_progress > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse mr-1 align-middle" />
          {batch.in_progress} running
        </span>
      )}
      {batch.pending > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
          {batch.pending} queued
        </span>
      )}
    </div>
  );
}

function SuccessRate({ batch }: { batch: BatchSummary }) {
  // Undefined until something has actually finished — a batch that's all
  // queued has a 0% success rate that means nothing.
  if (batch.completed + batch.failed === 0) {
    return <span className="text-primary-400">—</span>;
  }
  const value = batch.success_percentage;
  const tone =
    value >= 90 ? 'text-green-600' : value >= 70 ? 'text-amber-600' : 'text-red-600';
  return <span className={`font-mono font-medium ${tone}`}>{value.toFixed(0)}%</span>;
}

function startedAgo(value: string | null): string {
  if (!value) return '—';
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return '—';
  }
}

export function BatchRunsTable() {
  const { data } = usePolling(listActiveBatches, 3000);
  const batches = data ?? [];

  if (batches.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-2xl border border-primary-100 bg-white shadow-card">
      <div className="px-4 py-3 border-b border-primary-100 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
        <h2 className="text-sm font-semibold text-primary-900">Active Batch Runs</h2>
        <span className="text-xs text-primary-500">
          {batches.length} in progress
        </span>
      </div>
      <table className="data-table mobile-collapsible min-w-full text-sm">
        <thead className="bg-primary-50/60 text-primary-600 text-xs uppercase tracking-wider">
          <tr>
            <th className="px-4 py-3 text-left font-semibold">CSV File</th>
            <th className="px-4 py-3 text-left font-semibold">Scans</th>
            <th className="px-4 py-3 text-left font-semibold">Progress</th>
            <th className="px-4 py-3 text-left font-semibold">Status</th>
            <th className="px-4 py-3 text-left font-semibold">Success</th>
            <th className="px-4 py-3 text-left font-semibold">Started</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-primary-100">
          {batches.map((batch) => (
            <tr key={batch.batch_id} className="hover:bg-primary-50/40">
              <td className="px-4 py-3 text-primary-900 font-medium break-all" data-label="CSV File">
                {batch.filename}
              </td>
              <td className="px-4 py-3 font-mono text-primary-700" data-label="Scans">
                {batch.total_scans}
              </td>
              <td className="px-4 py-3" data-label="Progress">
                <ProgressBar percentage={batch.completion_percentage} />
              </td>
              <td className="px-4 py-3" data-label="Status">
                <StatusSummary batch={batch} />
              </td>
              <td className="px-4 py-3" data-label="Success">
                <SuccessRate batch={batch} />
              </td>
              <td className="px-4 py-3 text-primary-700" data-label="Started">
                {startedAgo(batch.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
